import * as _ from 'lodash-es'
import { observer } from 'mobx-react'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'

import { CheckBox } from '@/components/forms/CheckBox'
import { Toggle } from '@/components/forms/Toggle'
import { Button } from '@/components/ui/Button'
import { StateContainer } from '@/components/ui/StateContainer'
import { getDealerAccess, updateUser } from '@/services/user.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

import DealerSettings from './DealerSettings'

/**
 *
 * Settings
 *
 */
const Settings = observer(() => {
  // Context
  const {
    error: loadingError,
    isDealerUser,
    isImpersonating,
    isWpcUser,
    loading,
    getUpdatedUser,
    setCurrentUser,
    user,
  } = useContext(UserStoreContext)

  // State
  const [loadingSettingChange, setLoadingSettingChange] = useState(null)
  const [showDealerSettings, setShowDealerSettings] = useState(false)
  const [loadingDealerAccess, setLoadingDealerAccess] = useState(false)
  const [dealerAccess, setDealerAccess] = useState(null)

  const handleSuccesses = (message) => toast(message, 'success')
  const handleErrors = (message) => toast(message, 'error')

  useEffect(() => {
    getUpdatedUser(user.id)

    const getUpdatedDealerAccess = async () => {
      const access = await getDealerAccess(user.id, setLoadingDealerAccess, handleErrors)
      if (access) setDealerAccess(access)
    }

    getUpdatedDealerAccess()
  }, [])

  const configureDealerNotificationCopy = () => {
    const dealers = _.flatten(_.map(dealerAccess, (group) => group.dealers))

    const allDealersEnabled = _.every(dealers, (dealer) =>
      user.notificationSettings?.dealerNotificationsEnabled?.includes(dealer.id),
    )

    if (dealers.length === 1 && !allDealersEnabled) {
      return 'Currently receiving In-App and Email notifications for no Dealers.'
    }

    return allDealersEnabled
      ? 'Currently receiving In-App and Email notifications for all dealers.'
      : 'Currently receiving In-App and Email notifications for limited dealers.'
  }

  const inAppNotifications = useMemo(() => {
    const DEFAULT = [
      {
        label: 'RO Status Update',
        value: 'inAppReadyForReview',
      },
      {
        label: 'New RO Comment',
        value: 'inAppNewComment',
      },
      {
        label: 'New RO Attachment',
        value: 'inAppNewAttachment',
      },
    ]

    // WPC Users can configure notifications for billing
    if (isWpcUser) {
      DEFAULT.push({ label: 'Billing Details', value: 'inAppBilling' })
    }
    // Otherwise, only the RO Status Update email notification is available
    return DEFAULT
  }, [isDealerUser, isWpcUser])

  const emailNotifications = useMemo(() => {
    const STATUS_CHANGES = [
      {
        label: 'RO Status Update',
        value: 'emailReadyForReview',
      },
    ]

    // Dealer Users can opt in for all email notifications
    if (isDealerUser) {
      return [...STATUS_CHANGES]
    }

    // WPC Users can opt in for billing notifications
    if (isWpcUser) {
      STATUS_CHANGES.push({ label: 'Billing Details', value: 'emailBilling' })
    }

    // WPC Managers can opt in for a daily summary email
    if (user.ssoUserProfile?.role === 'Manager') {
      return [
        {
          label: 'Daily Summary',
          value: 'emailDailyReport',
        },
        ...STATUS_CHANGES,
      ]
    }

    // Otherwise, only the RO Status Update and billing details email notification are available
    return STATUS_CHANGES
  }, [isDealerUser, isWpcUser])

  const renderNotificationToggles = (options) =>
    _.map(options, (n) => (
      <div className="bg-brownGray-100 flex w-full rounded-lg p-6" key={n.value}>
        <Toggle
          disabled={loading || loadingSettingChange === n.value || isImpersonating}
          label={n.label}
          name={n.value}
          onChange={async (checked) => {
            setLoadingSettingChange(n.value)
            await updateUser(
              {
                id: user.id,
                notificationSettings: { [n.value]: checked },
              },
              handleErrors,
              () => {},
              () => handleSuccesses('Notification settings updated successfully.'),
              setCurrentUser,
            )
            setLoadingSettingChange(null)
          }}
          checked={user.notificationSettings && user.notificationSettings[n.value]}
        />
      </div>
    ))

  const renderSummaryEmailSettings = () => {
    const REPORTS = [
      {
        label: 'Daily Summary',
        value: 'emailDailyReport',
      },
      {
        label: 'Weekly Summary',
        value: 'emailWeeklyReport',
      },
      {
        label: 'Monthly Summary',
        value: 'emailMonthlyReport',
      },
    ]

    return (
      <div className="bg-brownGray-100 flex w-full flex-col justify-between gap-4 rounded-lg p-6 lg:flex-row">
        <Toggle
          disabled={loading || loadingSettingChange === 'summaryEmails' || isImpersonating}
          label="All Summary Emails"
          name="summaryEmails"
          onChange={async (checked) => {
            setLoadingSettingChange('summaryEmails')
            await updateUser(
              {
                id: user.id,
                notificationSettings: {
                  emailDailyReport: checked,
                  emailWeeklyReport: checked,
                  emailMonthlyReport: checked,
                },
              },
              handleErrors,
              () => {},
              () => handleSuccesses('Notification settings updated successfully.'),
              setCurrentUser,
            )
            setLoadingSettingChange(null)
          }}
          checked={
            user.notificationSettings &&
            _.every(REPORTS, (n) => user.notificationSettings[n.value])
          }
        />

        {_.map(REPORTS, (frequency) => (
          <CheckBox
            key={frequency.value}
            disabled={loading || loadingSettingChange === frequency.value || isImpersonating}
            label={frequency.label}
            name={frequency.value}
            onChange={async ({ target: { checked } }) => {
              setLoadingSettingChange(frequency.value)
              await updateUser(
                {
                  id: user.id,
                  notificationSettings: { [frequency.value]: checked },
                },
                handleErrors,
                () => {},
                () => handleSuccesses('Notification settings updated successfully.'),
                setCurrentUser,
              )
              setLoadingSettingChange(null)
            }}
            checked={user.notificationSettings && user.notificationSettings[frequency.value]}
          />
        ))}
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Settings</title>
      </Helmet>

      <StateContainer error={loadingError} loading={loading}>
        <div className="flex h-full w-full flex-col">
          <span className="text-midnight mb-5 self-start text-start text-2xl font-semibold leading-8 tracking-[-1px]">
            Settings
          </span>

          <div className="mx-8 flex flex-col">
            <div className="space-y-6">
              <div className="bg-brownGray-100 flex w-full flex-row items-center justify-between gap-4 rounded-lg p-4 px-6">
                <span className="text-sm font-semibold">{configureDealerNotificationCopy()}</span>
                <Button
                  disabled={isImpersonating}
                  label="Update"
                  onClick={() => setShowDealerSettings(true)}
                  size="sm"
                />
              </div>
            </div>
          </div>

          <div className="mx-8 mt-8 flex flex-col">
            <span className="text-charcoal-900 mb-5 self-start text-start text-lg font-semibold leading-8 tracking-[-1px]">
              In-App Notification Settings
            </span>

            <div className="space-y-6">{renderNotificationToggles(inAppNotifications)}</div>
          </div>

          <div className="mx-8 mt-8 flex flex-col">
            <span className="text-charcoal-900 mb-5 self-start text-start text-lg font-semibold leading-8 tracking-[-1px]">
              Email Notification Settings
            </span>

            <div className="space-y-6">
              {renderNotificationToggles(emailNotifications)}
              {isDealerUser && renderSummaryEmailSettings()}
            </div>
          </div>
        </div>
      </StateContainer>

      {showDealerSettings && (
        <DealerSettings
          dealerAccess={dealerAccess}
          loading={loadingDealerAccess}
          setShow={setShowDealerSettings}
          settings={user.notificationSettings}
          setCurrentUser={setCurrentUser}
          userId={user.id}
        />
      )}
    </>
  )
})

export default Settings
